import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, FileCheck, AlertCircle, BookOpen } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div style={{ marginTop: '70px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--bg-card)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-1" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
            Terms of Service
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            Last Updated: January 15, 2026
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          
          {/* Introduction */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <Scale size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                Agreement to Terms
              </h2>
            </div>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              These Terms of Service ("Terms") govern your access to and use of the LG Law Office website and services. By accessing or using our website, you agree to be bound by these Terms.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              If you do not agree to these Terms, please do not use our website or services.
            </p>
          </div>

          {/* No Attorney-Client Relationship */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <AlertCircle size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                No Attorney-Client Relationship
              </h2>
            </div>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              <strong>IMPORTANT:</strong> The information on this website is for general information purposes only. Nothing on this site should be taken as legal advice for any individual case or situation.
            </p>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              Viewing this website, submitting a form, sending an email, or calling our office does not create an attorney-client relationship. An attorney-client relationship is only formed when:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px' }}>
              <li>We agree to represent you</li>
              <li>You sign a written retainer agreement</li>
              <li>We confirm the representation in writing</li>
            </ul>
          </div>

          {/* Use of Website */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <BookOpen size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                Use of Website
              </h2>
            </div>
            
            <h3 className="heading-5" style={{ color: 'var(--text-white)', marginBottom: '16px', marginTop: '24px' }}>
              Permitted Use
            </h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
              You may use our website for lawful purposes only. You agree not to use the website:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px' }}>
              <li>In any way that violates any applicable law or regulation</li>
              <li>To transmit any harmful, threatening, or offensive material</li>
              <li>To impersonate any person or entity</li>
              <li>To interfere with the security or functionality of the website</li>
              <li>To collect or harvest information about other users</li>
            </ul>
          </div>

          {/* Intellectual Property */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Intellectual Property Rights
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              The content on this website, including text, graphics, logos, images, and software, is the property of LG Law Office or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              You may not reproduce, distribute, modify, or create derivative works from any content on this website without our express written permission.
            </p>
          </div>

          {/* Disclaimer of Warranties */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Disclaimer of Warranties
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              This website and its content are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px', marginBottom: '24px' }}>
              <li>Accuracy, completeness, or reliability of information</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
              <li>Uninterrupted or error-free operation</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              We do not warrant that the website will be free from viruses or other harmful components.
            </p>
          </div>

          {/* Limitation of Liability */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Limitation of Liability
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              To the fullest extent permitted by law, LG Law Office shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px' }}>
              <li>Your access to or use of (or inability to access or use) the website</li>
              <li>Any conduct or content of any third party on the website</li>
              <li>Any content obtained from the website</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
          </div>

          {/* Third-Party Links */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Third-Party Links
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              Our website may contain links to third-party websites or services that are not owned or controlled by LG Law Office. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
            </p>
          </div>

          {/* Indemnification */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Indemnification
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8' }}>
              You agree to defend, indemnify, and hold harmless LG Law Office and its officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with your access to or use of the website or your violation of these Terms.
            </p>
          </div>

          {/* Governing Law */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Governing Law
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8' }}>
              These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in California.
            </p>
          </div>

          {/* Changes to Terms */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Changes to Terms
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last Updated" date.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Your continued use of the website after changes are posted constitutes your acceptance of the modified Terms.
            </p>
          </div>

          {/* Contact Information */}
          <div style={{
            background: 'var(--bg-card)',
            padding: '32px',
            borderRadius: '12px',
            border: '2px solid var(--brand-primary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <FileCheck size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                Contact Us
              </h2>
            </div>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div style={{ color: 'var(--text-white)', lineHeight: '2' }}>
              <p><strong>LG Law Office</strong></p>
              <p>P.O. Box 2769, Pomona, CA 91769</p>
              <p>Phone: 1(909)397-7260</p>
            </div>
          </div>

          {/* Back to Home */}
          <div style={{ marginTop: '48px', textAlign: 'center' }}>
            <Link to="/" className="btn-primary">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;